import React, { useState, useEffect } from 'react';
import { useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import ListItemButton from '@mui/material/ListItemButton';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Search from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import { useTranslation } from 'next-i18next';
import { useFilters } from '../../Provider';
import { useApp } from '@web-kc/core/providers';

interface CompanyItem {
  id: number;
  name: string;
  fullName: string;
}

const LotCompanies: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');

  const isMP = useApp() === 'rti';
  const {
    selectedFilters,
    setField,
    companies,
    isLoadingCompany,
    debouncedSearchCompany,
  } = useFilters();

  const [selectedItems, setSelectedItems] = useState<CompanyItem[]>([]);

  const theme = useTheme();
  const { t } = useTranslation('tenders');

  useEffect(() => {
    if (companies?.content) {
      const toAdd = (companies.content as CompanyItem[])
        .filter((c) => selectedFilters?.companies?.includes(Number(c.id)))
        .filter((c) => !selectedItems.some((item) => item.id === Number(c.id)))
        .map((c) => ({ id: Number(c.id), name: c.name, fullName: c.fullName }));
      if (toAdd.length > 0) {
        setSelectedItems((prev) => [...prev, ...toAdd]);
      }
    }
  }, [companies?.content, selectedFilters.companies, selectedItems]);

  const handleAdd = (c: CompanyItem) => {
    if (!selectedFilters?.companies?.includes(c.id)) {
      setField('companies', [...(selectedFilters?.companies ?? []), c.id]);
      setSelectedItems((prev) => [...prev, c]);
    }
  };

  const handleRemove = (id: number) => {
    setField(
      'companies',
      selectedFilters?.companies?.filter((cId) => cId !== id),
    );
    setSelectedItems((prev) => prev.filter((item) => item.id !== id));
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    setInputValue(v);
    debouncedSearchCompany(v);
    setOpen(true);
  };

  const handleExcludeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setField('excludeCompanies', e.target.checked);
  };

  return (
    <Stack
      direction={{ lg: 'row', md: 'column', xs: 'column' }}
      mt={3.75}
      gap={{ xs: 1.25, lg: 2.5 }}
      alignItems="baseline"
    >
      <Box width="100%" maxWidth={120}>
        <Typography fontWeight={700}>{t('tenders.modal.companies')}</Typography>
      </Box>

      {isLoadingCompany ? (
        <CircularProgress />
      ) : (
        <Stack direction="column" width={'100%'} gap={2.5}>
          {isMP && (
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={selectedFilters.excludeCompanies}
                  onChange={handleExcludeChange}
                />
              }
              label={t('tenders.modal.excludedCompanies')}
              title={'123'}
            />
          )}

          <Box width="100%">
            <ClickAwayListener onClickAway={() => setOpen(false)}>
              <Box sx={{ position: 'relative' }}>
                <TextField
                  fullWidth
                  autoComplete="off"
                  value={inputValue}
                  onClick={() => setOpen(true)}
                  onChange={handleInputChange}
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search />
                        </InputAdornment>
                      ),
                    },
                  }}
                />

                {open && (
                  <Box
                    sx={{
                      overflow: 'auto',
                      backgroundColor: 'white',
                      zIndex: 999,
                      maxHeight: '200px',
                      position: 'absolute',
                      width: '100%',
                      borderRadius: '4px',
                      boxShadow: '0px 2px 5px -3px rgba(0,0,0,0.7)',
                    }}
                  >
                    <List disablePadding>
                      {companies?.content?.length === 0 ? (
                        <ListItem>
                          <ListItemText disableTypography>
                            {t('common:common.notFound')}
                          </ListItemText>
                        </ListItem>
                      ) : (
                        companies?.content
                          ?.filter(
                            (c) =>
                              !selectedFilters?.companies?.includes(
                                Number(c.id),
                              ),
                          )
                          .map((c) => (
                            <ListItemButton
                              key={c.id}
                              onClick={() =>
                                handleAdd({
                                  id: Number(c.id),
                                  name: `${c.name}`,
                                  fullName: `${c?.fullName}`,
                                })
                              }
                            >
                              <ListItemText disableTypography>
                                {c.fullName}
                              </ListItemText>
                            </ListItemButton>
                          ))
                      )}
                    </List>
                  </Box>
                )}
              </Box>
            </ClickAwayListener>

            <Stack direction="row" flexWrap="wrap" gap={1.25} mt={2.5}>
              {selectedFilters.companies?.map((catId) => {
                const name =
                  companies?.content?.find((c) => c.id === catId)?.fullName ||
                  '';
                return (
                  <Box
                    sx={{
                      maxHeight: { lg: '70px', xs: '60px', md: '60px' },
                      height: '100%',
                      border: `2px solid ${theme.palette.primary.light}`,
                      padding: '4px 8px',
                      borderRadius: '4px',
                    }}
                  >
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      gap={3.75}
                    >
                      <Typography>
                        {name.length > 30 ? `${name.slice(0, 30)}...` : name}
                      </Typography>
                      <IconButton
                        sx={{ padding: 0 }}
                        onClick={() => handleRemove(catId)}
                      >
                        <CloseIcon />
                      </IconButton>
                    </Stack>
                  </Box>
                );
              })}
            </Stack>
          </Box>
        </Stack>
      )}
    </Stack>
  );
};

export default LotCompanies;
